import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container, Typography, CssBaseline, Card, Box, Divider, List, ListItem,
  Table, TableBody, TableCell, TableHead, TableRow
} from '@mui/material';
import styled from 'styled-components';
import SEO from '../../components/SEO';
import { caseStudiesData } from '../../data/blogs-casestudy-Data.mjs';
import { Navbar } from '../Navbar/Navbar';
import { Footer } from '../Footer';
import { PUBLIC_SITE_URL } from '../../config/site';


// Styled section wrapper with consistent spacing
const Section = styled(Box)`
  margin-top: 40px;
  margin-bottom: 40px;
`;
const FormSide = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const TopMargin = styled.div`
  margin-top: 100px;
`;

const StyledCard = styled.div`
  background-color: #f7f7f7;
  padding: 0px;
 box-shadow: 0 0 20px rgba(0, 0, 0, 0.1);
`;

// Styled subheadings for better visual hierarchy
const SubHeading = styled(Typography)`
  margin-bottom: 16px;
  font-weight: 600;
`;


const BlogDetailPage = () => {
  const navigate = useNavigate();
  const { slug } = useParams();

  const currentCaseStudy = caseStudiesData.find(study => study.slug === slug);

  useEffect(() => {
    if (!currentCaseStudy) {
      navigate('/*');
    }
  }, [slug, navigate, currentCaseStudy]);


  return (
    <>
      <SEO
        title={currentCaseStudy?.seoTitle || "Case Study | Watchnod"}
        description={currentCaseStudy?.description || "Explore real-world case studies showing how businesses improve uptime, performance, and reliability using WatchNod server monitoring solutions."}
        keywords={currentCaseStudy?.keywords || ""}
        url={`/case-studies/${slug}`}
        type="website"
      />
      <Navbar />
      <TopMargin />
      <CssBaseline />
      <StyledCard>
        <Card>
          <FormSide>

            {currentCaseStudy?.contentKey === "caseStudy-1" && (
              <Container maxWidth="lg">
                {/* Case Study Title */}
                <Typography
                  variant="h3"
                  gutterBottom
                  textAlign="center"
                  sx={{ my: 6 }}
                >
                  How a Digital Petroleum Retail Startup Achieved 24/7 Infrastructure Visibility with WatchNod
                </Typography>

                {/* About the Client */}
                <Section>
                  <SubHeading variant="h5">About the Client – A Digital Pioneer in Petroleum Commerce</SubHeading>
                  <Typography variant="body1" paragraph>
                    Our client is a forward-thinking technology startup revolutionizing how petroleum products are distributed and managed through digital channels.
                    Their core platform is a real-time web application running on a Windows Server stack — powered by IIS for web services and MS-SQL for data operations.
                    Uptime, speed, and reliability are essential to their service delivery and customer satisfaction.
                  </Typography>
                </Section>

                <Divider />

                {/* The Challenge */}
                <Section>
                  <SubHeading variant="h5">
                    ⚠️ The Challenge: Blind Spots in Critical Infrastructure
                  </SubHeading>

                  <Typography variant="body1" paragraph>
                    Before adopting WatchNod, the client faced several monitoring limitations that hindered system reliability:
                  </Typography>

                  <Box
                    component="ul"
                    sx={{
                      pl: { xs: 2, sm: 3 },
                      pr: { xs: 1, sm: 0 },
                      m: 0,
                      listStyleType: "none",
                    }}
                  >
                    {[
                      "No Live Monitoring: Performance bottlenecks and outages weren’t caught in real time.",
                      "Manual Checks: Key services like IIS and SQL Server lacked automated health tracking.",
                      "No Alerting System: No way to catch high CPU usage, disk saturation, or memory pressure early.",
                      "Lack of Historical Data: Teams couldn't analyze past patterns to diagnose recurring issues.",
                      "Reactive Troubleshooting: Incidents were handled only after users were impacted.",
                    ].map((text, index) => (
                      <li key={index} style={{ marginBottom: "8px" }}>
                        <Typography
                          variant="body1"
                          sx={{ fontSize: { xs: "0.9rem", sm: "1rem" } }}
                        >
                          <strong>• {text.split(":")[0]}:</strong>
                          &nbsp;{text.split(":")[1]}
                        </Typography>
                      </li>
                    ))}
                  </Box>
                </Section>


                <Divider />

                {/* Why WatchNod */}
                <Section>
                  <SubHeading variant="h5">💡 Why WatchNod Was the Right Fit</SubHeading>
                  <List sx={{ pl: 3 }}>
                    <ListItem>• Effortless Setup using Windows Exporter + Prometheus stack.</ListItem>
                    <ListItem>• Real-Time Dashboards built with Grafana.</ListItem>
                    <ListItem>• Custom Alerts to detect critical service or resource issues.</ListItem>
                    <ListItem>• Modular Architecture adaptable to changing infrastructure needs.</ListItem>
                    <ListItem>• Historical Metrics for deep-dive analysis and performance reviews.</ListItem>
                  </List>
                </Section>

                <Divider />

                {/* Implementation & Impact */}
                <Section>
                  <SubHeading variant="h5">⚙️ Implementation & Impact: From Blind Spots to Full Visibility</SubHeading>
                  <Typography variant="subtitle1" gutterBottom>
                    24/7 Monitoring Across the Stack
                  </Typography>
                  <Typography variant="body1" paragraph>
                    Real-time tracking of CPU, memory, disk, and network, applied across both application and database servers.
                  </Typography>

                  <Typography variant="subtitle1" gutterBottom>
                    Smarter Alerts, Faster Response
                  </Typography>
                  <Typography variant="body1" paragraph>
                    Custom thresholds triggered alerts for anomalies like CPU spikes or IIS failures.
                    Teams received real-time notifications — preventing major disruptions.
                  </Typography>

                  <Typography variant="subtitle1" gutterBottom>
                    Clean, Insightful Dashboards
                  </Typography>
                  <Typography variant="body1" paragraph>
                    Grafana-based visuals offered live system health overviews, including data like request loads, 404 errors, SQL query health, and uptime.
                  </Typography>
                </Section>

                <Divider />

                {/* Business Impact */}
                <Section>
                  <SubHeading variant="h5">📈 Measurable Business Impact</SubHeading>

                  <Table sx={{ mt: 2, border: '1px solid #ddd' }}>
                    <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
                      <TableRow>
                        <TableCell><strong>Outcome</strong></TableCell>
                        <TableCell><strong>Benefit</strong></TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      <TableRow>
                        <TableCell>Zero Downtime</TableCell>
                        <TableCell>Maintained uninterrupted service delivery</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Resource Optimisation</TableCell>
                        <TableCell>Performance improved via real usage insights</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Developer Visibility</TableCell>
                        <TableCell>Teams had clear insight into infrastructure health</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Proactive Alerts</TableCell>
                        <TableCell>Resolved issues before they escalated</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Historical Insights</TableCell>
                        <TableCell>Enabled smarter capacity and trend planning</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </Section>


                <Divider />

                {/* Testimonial */}
                <Section>
                  <SubHeading variant="h5">💬 Testimonial</SubHeading>
                  <Typography variant="body1" paragraph>
                    “With WatchNod, we shifted from firefighting to foresight. The difference in stability and speed is clear.”
                  </Typography>
                  <Typography variant="subtitle1" gutterBottom>
                    — Infrastructure Lead, Petroleum eCommerce Startup
                  </Typography>
                </Section>

                <Divider />

                {/* Call to Action */}
                <Section>
                  <SubHeading variant="h5">🚀 Take Control of Your Infrastructure Today</SubHeading>
                  <Typography variant="body1" paragraph>
                    WatchNod gives modern teams the tools to monitor, alert, and act — seamlessly.
                  </Typography>
                  <Typography variant="body1" paragraph>
                    🔗 Visit: <a href={PUBLIC_SITE_URL} target="_blank" rel="noopener noreferrer">{PUBLIC_SITE_URL}</a>
                  </Typography>
                  <Typography variant="body1">
                    📩 Start your monitoring journey now.
                  </Typography>
                </Section>
              </Container>
            )}


            {currentCaseStudy?.contentKey === "caseStudy-2" && (
              <Container maxWidth="lg">
                {/* Case Study Title */}
                <Typography
                  variant="h3"
                  gutterBottom
                  textAlign="center"
                  sx={{ my: 6 }}
                >
                  E-Commerce Platform Avoids Downtime During Flash Sales with WatchNod
                </Typography>

                {/* About the Client */}
                <Section>
                  <SubHeading variant="h5">About the Client – Scaling Under High Traffic</SubHeading>
                  <Typography variant="body1" paragraph>
                    A mid-sized eCommerce brand running sales-driven campaigns and high-traffic festive offers.
                    Their tech stack includes a Node.js-based frontend, MongoDB backend, and a Linux-based server hosted on AWS.
                  </Typography>
                </Section>

                <Divider />

                {/* The Challenge */}
                <Section>
                  <SubHeading variant="h5">
                    ⚠️ The Challenge: Flash Sale Traffic Overload
                  </SubHeading>

                  <Box
                    component="ul"
                    sx={{
                      pl: { xs: 2, sm: 3 },
                      pr: { xs: 1, sm: 0 },
                      m: 0,
                      listStyleType: "none",
                    }}
                  >
                    {[
                      "Traffic Spikes: Flash sales crashed servers due to overload.",
                      "No Live Metrics: No real-time tracking of CPU or memory usage.",
                      "No Alert System: Product API failures went unnoticed until too late.",
                      "Missed Revenue: Checkout outages directly impacted sales during peak events.",
                    ].map((text, index) => (
                      <li key={index} style={{ marginBottom: "8px" }}>
                        <Typography
                          variant="body1"
                          sx={{ fontSize: { xs: "0.9rem", sm: "1rem" } }}
                        >
                          <strong>• {text.split(":")[0]}:</strong>
                          &nbsp;{text.split(":")[1]}
                        </Typography>
                      </li>
                    ))}
                  </Box>
                </Section>


                <Divider />

                {/* Why WatchNod */}
                <Section>
                  <SubHeading variant="h5">💡 WatchNod Implementation</SubHeading>
                  <List sx={{ pl: 3 }}>
                    <ListItem>• Integrated Linux Exporter and MongoDB exporters.</ListItem>
                    <ListItem>• Set up real-time dashboards to monitor API health and resource usage.</ListItem>
                    <ListItem>• Configured alerts for request failures and high memory consumption.</ListItem>
                    <ListItem>• Created historical logs for identifying peak traffic times.</ListItem>
                  </List>
                </Section>

                <Divider />

                {/* Business Impact */}
                <Section>
                  <SubHeading variant="h5">📈 Measurable Business Impact</SubHeading>

                  <Table sx={{ mt: 2, border: '1px solid #ddd' }}>
                    <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
                      <TableRow>
                        <TableCell><strong>Outcome</strong></TableCell>
                        <TableCell><strong>Benefit</strong></TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      <TableRow>
                        <TableCell>Zero Downtime</TableCell>
                        <TableCell>Customers could complete purchases without failure</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Increased Conversions</TableCell>
                        <TableCell>Checkout success rate improved during flash sales</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>API Monitoring</TableCell>
                        <TableCell>Real-time visibility into product/service endpoints</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Instant Alerts</TableCell>
                        <TableCell>Quickly caught and fixed performance issues</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Load Trend Analysis</TableCell>
                        <TableCell>Helped plan infrastructure ahead of traffic spikes</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </Section>

                <Divider />

                {/* Testimonial */}
                <Section>
                  <SubHeading variant="h5">💬 Testimonial</SubHeading>
                  <Typography variant="body1" paragraph>
                    “With WatchNod, we not only prevented flash sale crashes but actually increased checkout conversions.
                    It's our silent partner during high-pressure events.”
                  </Typography>
                </Section>

                <Divider />

                {/* Call to Action */}
                <Section>
                  <SubHeading variant="h5">🚀 Take Control of Your eCommerce Platform Today</SubHeading>
                  <Typography variant="body1" paragraph>
                    WatchNod gives modern teams the tools to monitor, alert, and act — seamlessly.
                  </Typography>
                  <Typography variant="body1" paragraph>
                    🔗 Visit: <a href={PUBLIC_SITE_URL} target="_blank" rel="noopener noreferrer">{PUBLIC_SITE_URL}</a>
                  </Typography>
                  <Typography variant="body1">
                    📩 Start your monitoring journey now.
                  </Typography>
                </Section>
              </Container>
            )}



            {currentCaseStudy?.contentKey === "caseStudy-3" && (
              <Container maxWidth="lg">
                {/* Case Study Title */}
                <Typography
                  variant="h3"
                  gutterBottom
                  textAlign="center"
                  sx={{ my: 6 }}
                >
                  SaaS Startup Gains Early Stability with Scalable Monitoring
                </Typography>

                {/* About the Client */}
                <Section>
                  <SubHeading variant="h5">About the Client – Fast-Growing SaaS on Microservices</SubHeading>
                  <Typography variant="body1" paragraph>
                    A fast-growing SaaS startup building a project management tool.
                    The product runs on a microservices architecture using Docker and Kubernetes, hosted on DigitalOcean.
                  </Typography>
                </Section>

                <Divider />

                {/* The Challenge */}
                <Section>
                  <SubHeading variant="h5">
                    ⚠️ The Challenge: Scaling Without Clear Visibility
                  </SubHeading>

                  <Box
                    component="ul"
                    sx={{
                      pl: { xs: 2, sm: 3 },
                      pr: { xs: 1, sm: 0 },
                      m: 0,
                      listStyleType: "none",
                    }}
                  >
                    {[
                      "No Centralized Visibility: Hard to track health across pods and services.",
                      "Log Correlation Issues: Difficult to connect service crashes to root causes.",
                      "Missed Early Warnings: Memory leaks and restarts went unnoticed until too late.",
                      "High Cost of Existing Tools: Solutions were too expensive or complex to scale effectively.",
                    ].map((text, index) => {
                      const [title, description] = text.split(":");
                      return (
                        <li key={index} style={{ marginBottom: "8px" }}>
                          <Typography
                            variant="body1"
                            sx={{ fontSize: { xs: "0.9rem", sm: "1rem" } }}
                          >
                            <strong>• {title}:</strong>&nbsp;{description}
                          </Typography>
                        </li>
                      );
                    })}
                  </Box>
                </Section>


                <Divider />

                {/* WatchNod Implementation */}
                <Section>
                  <SubHeading variant="h5">💡 WatchNod Implementation</SubHeading>
                  <List sx={{ pl: 3 }}>
                    <ListItem>• Deployed Kube Prometheus Stack with WatchNod for Kubernetes monitoring.</ListItem>
                    <ListItem>• Built dashboards tracking pod health, container restarts, and CPU/memory usage.</ListItem>
                    <ListItem>• Integrated Slack alerts for instant issue notifications.</ListItem>
                    <ListItem>• Enabled long-term metrics storage for identifying patterns and trends.</ListItem>
                  </List>
                </Section>

                <Divider />

                {/* Measurable Business Impact */}
                <Section>
                  <SubHeading variant="h5">📈 Measurable Business Impact</SubHeading>

                  <Table sx={{ mt: 2, border: '1px solid #ddd' }}>
                    <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
                      <TableRow>
                        <TableCell><strong>Outcome</strong></TableCell>
                        <TableCell><strong>Benefit</strong></TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      <TableRow>
                        <TableCell>Faster Issue Detection</TableCell>
                        <TableCell>Reduced response time from hours to minutes</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Scalable Monitoring</TableCell>
                        <TableCell>Easily covered all microservices & containers</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Pod-Level Visibility</TableCell>
                        <TableCell>Prevented crashes by tracking memory leaks and restarts</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Dev Team Collaboration</TableCell>
                        <TableCell>Unified monitoring view improved cross-team coordination</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Performance Trends</TableCell>
                        <TableCell>Historical data informed scaling and feature planning</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </Section>

                <Divider />

                {/* Testimonial */}
                <Section>
                  <SubHeading variant="h5">💬 Testimonial</SubHeading>
                  <Typography variant="body1" paragraph>
                    “As a startup, we needed affordable observability without complexity.
                    WatchNod fit like a glove. It gave us peace of mind — and velocity.”
                  </Typography>
                </Section>

                <Divider />

                {/* Call to Action */}
                <Section>
                  <SubHeading variant="h5">🚀 Take Control of Your SaaS Monitoring Today</SubHeading>
                  <Typography variant="body1" paragraph>
                    WatchNod gives modern teams the tools to monitor, alert, and act — seamlessly.
                  </Typography>
                  <Typography variant="body1" paragraph>
                    🔗 Visit: <a href={PUBLIC_SITE_URL} target="_blank" rel="noopener noreferrer">{PUBLIC_SITE_URL}</a>
                  </Typography>
                  <Typography variant="body1">
                    📩 Start your monitoring journey now.
                  </Typography>
                </Section>
              </Container>
            )}


            {currentCaseStudy?.contentKey === "caseStudy-4" && (
              <Container maxWidth="lg">
                {/* Case Study Title */}
                <Typography
                  variant="h3"
                  gutterBottom
                  textAlign="center"
                  sx={{ my: 6 }}
                >
                  Freelancer DevOps Engineer Offers Premium Monitoring as a Service
                </Typography>

                {/* About the Client */}
                <Section>
                  <SubHeading variant="h5">About the Client – Freelance DevOps Consultant</SubHeading>
                  <Typography variant="body1" paragraph>
                    A freelance DevOps consultant managing web apps and cloud deployments for multiple small businesses.
                    Each client had different hosting environments, tools, and monitoring needs.
                  </Typography>
                </Section>

                <Divider />

                {/* The Challenge */}
                <Section>
                  <SubHeading variant="h5">
                    ⚠️ The Challenge: Managing Multiple Clients Without a Unified View
                  </SubHeading>

                  <Box
                    component="ul"
                    sx={{
                      pl: { xs: 2, sm: 3 },
                      pr: { xs: 1, sm: 0 },
                      m: 0,
                      listStyleType: "none",
                    }}
                  >
                    {[
                      "Juggling 5+ clients: Each had unique server setups and tools.",
                      "No unified platform: Monitoring all servers required switching between multiple dashboards.",
                      "Proof of uptime: Difficult to provide clients with clear uptime performance reports.",
                      "Manual checks: Server status had to be verified individually for each project.",
                    ].map((text, index) => {
                      const [title, description] = text.split(":");
                      return (
                        <li key={index} style={{ marginBottom: "8px" }}>
                          <Typography
                            variant="body1"
                            sx={{ fontSize: { xs: "0.9rem", sm: "1rem" } }}
                          >
                            <strong>• {title}:</strong>&nbsp;{description}
                          </Typography>
                        </li>
                      );
                    })}
                  </Box>
                </Section>

                <Divider />

                {/* WatchNod Implementation */}
                <Section>
                  <SubHeading variant="h5">💡 WatchNod Implementation</SubHeading>
                  <List sx={{ pl: 3 }}>
                    <ListItem>• Set up multi-tenant monitoring with segregated dashboards for each client.</ListItem>
                    <ListItem>• Enabled client-specific alerts and automated email reports.</ListItem>
                    <ListItem>• Shared live dashboard URLs to provide real-time transparency.</ListItem>
                    <ListItem>• Monitored uptime, memory usage, load, and domain expiration alerts in one place.</ListItem>
                  </List>
                </Section>

                <Divider />

                {/* Measurable Business Impact */}
                <Section>
                  <SubHeading variant="h5">📈 Measurable Business Impact</SubHeading>

                  <Table sx={{ mt: 2, border: '1px solid #ddd' }}>
                    <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
                      <TableRow>
                        <TableCell><strong>Outcome</strong></TableCell>
                        <TableCell><strong>Benefit</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell>Centralized Monitoring</TableCell>
                        <TableCell>Managed multiple clients from one platform</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Client Transparency</TableCell>
                        <TableCell>Shared dashboards built trust and credibility</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Uptime Reporting</TableCell>
                        <TableCell>Demonstrated measurable service value to clients</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Monetized Offering</TableCell>
                        <TableCell>Added "monitoring-as-a-service" to freelancing toolkit</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Time Saved</TableCell>
                        <TableCell>Reduced manual server checks by 80%</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </Section>

                <Divider />

                {/* Testimonial */}
                <Section>
                  <SubHeading variant="h5">💬 Testimonial</SubHeading>
                  <Typography variant="body1" paragraph>
                    “WatchNod became my secret weapon. Clients are amazed I give them enterprise-level uptime monitoring. It pays for itself.”
                  </Typography>
                </Section>

                <Divider />

                {/* Call to Action */}
                <Section>
                  <SubHeading variant="h5">🚀 Take Control of Your Infrastructure Today</SubHeading>
                  <Typography variant="body1" paragraph>
                    WatchNod gives modern teams the tools to monitor, alert, and act — seamlessly.
                  </Typography>
                  <Typography variant="body1" paragraph>
                    🔗 Visit: <a href={PUBLIC_SITE_URL} target="_blank" rel="noopener noreferrer">{PUBLIC_SITE_URL}</a>
                  </Typography>
                  <Typography variant="body1">
                    📩 Start your monitoring journey now.
                  </Typography>
                </Section>
              </Container>
            )}



          </FormSide>
        </Card>
      </StyledCard >
      <Box sx={{ position: 'relative', bottom: 0, width: '100%' }}>
        <Footer />
      </Box>
    </>
  );
};

export default BlogDetailPage;
